import { ReactNode } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import _ from 'lodash';

import { UserSpendTier } from '../../../../../../__gqltypes__';
import { SolidBadge } from '../../../../../../utils/components/SolidBadge';
import {
  formatPushUtc,
  parsePushIdList,
  PushBroadcastData,
  PushBroadcastDraft,
  PushBroadcastOptions,
  pushLanguageName,
  PushPerson,
  pushPersonLabel,
} from '../utils';
import { PushLockNote } from './PushLockNote';

type Props = {
  draft: PushBroadcastDraft;
  onChange: (draft: PushBroadcastDraft) => void;
  options: PushBroadcastOptions;
  broadcast: PushBroadcastData | null;
  people: ReadonlyMap<string, PushPerson>;
  disabled: boolean;
  // Why every control is off (not a draft), shown at the top of the card; null when editable.
  lockedReason: string | null;
  // Unsaved edits: the Count button saves them first (the count reads the saved filters).
  isDirty: boolean;
  isFiltersDirty: boolean;
  isContentDirty: boolean;
  counting: boolean;
  onCount: () => void;
  // The send, right under the count: who gets it, then send it, read as one decision. Null when there is none.
  sendSection?: ReactNode;
};

const SPEND_TIERS = Object.values(UserSpendTier);

function IdListField({
  id,
  label,
  help,
  value,
  maxListed,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  value: string;
  maxListed: number;
  disabled: boolean;
  onChange: (text: string) => void;
}): JSX.Element {
  const count = parsePushIdList(value).length;
  return (
    <Form.Group controlId={id} className="mb-2">
      <div className="d-flex justify-content-between align-items-end">
        <Form.Label className="mb-1">{label}</Form.Label>
        <small className={count > maxListed ? 'text-danger font-weight-bold' : 'text-muted'}>
          {count} / {maxListed} accounts
        </small>
      </div>
      <Form.Control
        as="textarea"
        rows={2}
        value={value}
        disabled={disabled}
        placeholder="One user id per line"
        onChange={(event) => onChange(event.target.value)}
      />
      <Form.Text muted>{help}</Form.Text>
    </Form.Group>
  );
}

/**
 * Who receives it: the criteria the backend exposes, then the count, fetched only when the button is pressed
 * (the Mass Gifting onRefreshNumber / isRecipientPreviewDirty pattern), never on edit or on load; then the send.
 */
export function PushAudience({
  draft,
  onChange,
  options,
  broadcast,
  people,
  disabled,
  lockedReason,
  isDirty,
  isFiltersDirty,
  isContentDirty,
  counting,
  onCount,
  sendSection = null,
}: Props): JSX.Element {
  const { pushBroadcastLimits: limits, pushBroadcastLanguages: languages } = options;
  const { filters } = draft;
  const setFilters = (patch: Partial<PushBroadcastDraft['filters']>) =>
    onChange({ ...draft, filters: { ...filters, ...patch } });
  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((other) => other !== value) : [...list, value];

  const audience = broadcast?.audience ?? null;
  const isStale = !!audience && (isFiltersDirty || !audience.isCurrent);
  const byLanguage = _.orderBy(audience?.byLanguage ?? [], ['count'], ['desc']);

  return (
    <Card className="mb-3">
      <Card.Header className="font-weight-bold">{sendSection ? 'Audience and send' : 'Audience'}</Card.Header>
      <Card.Body>
        <PushLockNote reason={lockedReason} />
        <Row>
          <Col md={6}>
            <Form.Group controlId="push-audience-days" className="mb-2">
              <Form.Label className="mb-1">
                Played in the last … days <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={limits.maxDaysSinceLastOnline}
                value={filters.daysSinceLastOnline}
                disabled={disabled}
                onChange={(event) => setFilters({ daysSinceLastOnline: event.target.value })}
              />
              <Form.Text muted>
                1 to {limits.maxDaysSinceLastOnline}, default {limits.defaultDaysSinceLastOnline}. A broadcast never
                reaches the whole player base.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="push-audience-platform" className="mb-2">
              <Form.Label className="mb-1">Platform</Form.Label>
              <Form.Control
                as="select"
                value={filters.platform}
                disabled={disabled}
                onChange={(event) => setFilters({ platform: event.target.value })}
              >
                <option value="">Any</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="push-audience-app-version" className="mb-2">
              <Form.Label className="mb-1">Minimum app version</Form.Label>
              <Form.Control
                value={filters.appVersion}
                placeholder="e.g. 3.40.0"
                disabled={disabled}
                onChange={(event) => setFilters({ appVersion: event.target.value })}
              />
              <Form.Text muted>Of a device with a push token.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="push-audience-install" className="mb-2">
              <Form.Label className="mb-1">Account created less than … days ago</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={filters.daysSinceInstall}
                disabled={disabled}
                onChange={(event) => setFilters({ daysSinceInstall: event.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="push-audience-level" className="mb-2">
              <Form.Label className="mb-1">Minimum level</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={filters.minLevel}
                disabled={disabled}
                onChange={(event) => setFilters({ minLevel: event.target.value })}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-2">
          <Form.Label className="mb-1">Spend tiers (none = every tier)</Form.Label>
          <div>
            {SPEND_TIERS.map((tier) => (
              <Form.Check
                inline
                key={tier}
                id={`push-audience-tier-${tier}`}
                type="checkbox"
                label={tier}
                checked={filters.spendTiers.includes(tier)}
                disabled={disabled}
                onChange={() => setFilters({ spendTiers: toggle(filters.spendTiers, tier) })}
              />
            ))}
          </div>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label className="mb-1">Game languages (none = every language)</Form.Label>
          <div>
            {languages.map(({ code, name }) => (
              <Form.Check
                inline
                key={code}
                id={`push-audience-language-${code}`}
                type="checkbox"
                label={name}
                checked={filters.gameLanguages.includes(code)}
                disabled={disabled}
                onChange={() => setFilters({ gameLanguages: toggle(filters.gameLanguages, code) })}
              />
            ))}
          </div>
        </Form.Group>

        <Row>
          <Col md={6}>
            <IdListField
              id="push-audience-user-ids"
              label="Only these accounts (allowlist)"
              help="To test: send to your own one-account allowlist, check the phone, then Duplicate and set the real audience."
              value={filters.userIds}
              maxListed={limits.maxListedUsers}
              disabled={disabled}
              onChange={(userIds) => setFilters({ userIds })}
            />
          </Col>
          <Col md={6}>
            <IdListField
              id="push-audience-excluded-user-ids"
              label="Never these accounts (denylist)"
              help="Wins over every other criterion."
              value={filters.excludedUserIds}
              maxListed={limits.maxListedUsers}
              disabled={disabled}
              onChange={(excludedUserIds) => setFilters({ excludedUserIds })}
            />
          </Col>
        </Row>
        <Form.Text muted className="mb-3">
          Always left out: kids accounts, and players who turned the opt-out category off.
        </Form.Text>

        <div className="border rounded p-2 bg-light">
          <div className="d-flex align-items-center mb-2">
            <Button size="sm" className="mr-2" disabled={disabled || counting || !broadcast} onClick={onCount}>
              {counting ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
              {isDirty && broadcast ? 'Save and count audience' : 'Count audience'}
            </Button>
            {!broadcast ? <small className="text-muted">Create the draft first.</small> : null}
            {isStale ? <SolidBadge tone="amber">Out of date: count again</SolidBadge> : null}
          </div>

          {audience ? (
            <div className={isStale ? 'text-muted' : ''}>
              <div className="font-weight-bold">
                ≈ {audience.count.toLocaleString()} players with a device · as of {formatPushUtc(audience.computedAt)}{' '}
                by {pushPersonLabel(audience.computedBy, people)} · approximate: the send recomputes
              </div>
              <div className="small mb-2">
                An upper bound: players who turned notifications off in their phone settings cannot be seen from here,
                so fewer will actually get it.
              </div>
              {byLanguage.length > 0 ? (
                <Table size="sm" className="mb-1 bg-white">
                  <thead>
                    <tr>
                      <th>Player language</th>
                      <th className="text-right">Players</th>
                      <th>Receives</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byLanguage.map(({ language, count, hasTranslation }) => (
                      <tr key={language}>
                        <td>{pushLanguageName(language, languages)}</td>
                        <td className="text-right">{count.toLocaleString()}</td>
                        <td>
                          {hasTranslation ? (
                            <SolidBadge tone="green">translated</SolidBadge>
                          ) : (
                            <SolidBadge tone="grey">English</SolidBadge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : null}
              <div className="font-weight-bold">
                {audience.receivingEnglishFallback.toLocaleString()} players will receive English
              </div>
              {isContentDirty ? (
                <Alert variant="info" className="mt-2 mb-0 py-1 small">
                  Translated / English reflects the saved texts: save to update it.
                </Alert>
              ) : null}
            </div>
          ) : (
            <div className="text-muted">
              Not counted yet. The count runs only when you press the button, on the saved criteria.
            </div>
          )}
        </div>
        {sendSection ? <div className="border-top pt-3 mt-3">{sendSection}</div> : null}
      </Card.Body>
    </Card>
  );
}
